// Controlled TransportType picker for the CONTAINER request editor (#701).
//
// Renders the active TransportType rows (sorted by `sortOrder, code`) as a
// dropdown. On selection, the parent's `onChange(id)` updates the draft
// scratchpad's `transportTypeId`; the editor's completeness check rejects
// CONTAINER drafts with a null value (`useDraftCompleteness`).
//
// A display-only capacity hint sits underneath the dropdown:
//   "60–80 m³ · 20–30 t"  (volume range · payload range)
// driven from the selected row's `volumeMin/MaxM3` and `payloadMin/MaxT`.
// No client-side capacity enforcement — the hint is informational per the
// #701 acceptance criteria.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { listTransportTypes } from '../../../api/transport-types.js';
import { useLocale } from '../../../i18n/useLocale.js';

type Props = {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
};

export function TransportTypePicker({ value, onChange, disabled }: Props): JSX.Element {
  const { t } = useTranslation(['requests']);
  const { locale } = useLocale();
  const query = useQuery({
    queryKey: ['transport-types', 'active'],
    // WHY (#701): the picker only shows active rows. Backend supports
    //   `active=true` query string so the wire trims archived rows for
    //   us; no client-side filter needed.
    queryFn: () => listTransportTypes({ active: true, limit: 100 }),
    staleTime: 60_000,
  });
  const rows = query.data?.items ?? [];
  const selected = rows.find((r) => r.id === value) ?? null;
  const displayName = (id: string): string => {
    const r = rows.find((x) => x.id === id);
    if (!r) return id;
    // WHY: TransportType only stores nameRu/nameEn; zh-Hans inherits Russian
    //   as the catalog fallback (no third locale on the row yet).
    return locale === 'en' ? r.nameEn : r.nameRu;
  };
  const hint = selected
    ? t('requests:draft.container.capacityHint', {
        volumeMin: selected.volumeMinM3,
        volumeMax: selected.volumeMaxM3,
        payloadMin: selected.payloadMinT,
        payloadMax: selected.payloadMaxT,
      })
    : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, color: 'var(--fg-2)' }}>
        {t('requests:draft.container.transportTypeLabel')}
      </label>
      <select
        data-testid="draft-transport-type-picker"
        value={value ?? ''}
        disabled={disabled === true || query.isPending}
        onChange={(e) => {
          const id = e.target.value;
          onChange(id === '' ? null : id);
        }}
        style={{
          padding: '6px 8px',
          fontSize: 14,
          border: '1px solid var(--border-1)',
          borderRadius: 6,
          background: 'var(--bg-0)',
          color: 'var(--fg-1)',
        }}
      >
        <option value="">{t('requests:draft.container.pickerPlaceholder')}</option>
        {rows.map((r) => (
          <option key={r.id} value={r.id}>
            {displayName(r.id)}
          </option>
        ))}
      </select>
      {hint !== null && (
        <span
          data-testid="draft-transport-type-hint"
          style={{ fontSize: 12, color: 'var(--fg-3)' }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}
